import { type ReactNode, useMemo, useState } from 'react';

import { JsonField, RichTextField, TextField, Textarea } from '~/components/form';
import DesignPickerModal from '~/components/pages/notifications/templates/DesignPickerModal';
import LinkedDesign from '~/components/pages/notifications/templates/LinkedDesign';
import TemplateTestPanel from '~/components/pages/notifications/templates/TemplateTestPanel';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardSection,
  FormActions,
  Tabs,
  TagsEditor,
} from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useUrlParams } from '~/hooks/useUrlParams';
import { KEY_DASH_HINT } from '~/lib/ai/keyFormat';
import {
  CHANNEL_LABELS,
  type ChannelFormValues,
  fieldErrorsFor,
  renameTemplate,
  TEMPLATE_CHANNELS,
  type TemplateFormValues,
  templateSchema,
} from '~/lib/notification/templateForm';
import type { NotificationChannel } from '~/modules/query/notification/types';
import { routes } from '~/app/routes';

const FORM_ID = 'template-form';
const DEFAULT_CHANNEL: NotificationChannel = 'email';

const TABS = TEMPLATE_CHANNELS.map((id) => ({ id, label: CHANNEL_LABELS[id] }));

interface TemplateFormProps {
  /** The saved template's id; absent on create. A linked design is test-sent by it. */
  templateId?: string;
  draft: TemplateFormValues;
  patch: (changes: Partial<TemplateFormValues>) => void;
  isNew: boolean;
  /** An old version: every field is read-only and there is no Save. */
  readOnly?: boolean;
  dirty: boolean;
  busy: boolean;
  /** A failed save, as a sentence for the `Alert`. */
  error: string | null;
  /** A key that is already taken (globally or for a listed customer), shown on the key field. */
  keyError?: string;
  /** Beside the title: the status chip. */
  badge?: ReactNode;
  /** Right end of the card header: the version picker. */
  headerActions?: ReactNode;
  description?: string;
  /** Called once the form is valid. */
  onSubmit: () => void;
}

/**
 * The template form, with the preview-and-test panel in the right column (the placement editor's
 * layout), shared by `/notifications/templates/new` and `/:id`.
 */
export default function TemplateForm({
  templateId,
  draft,
  patch,
  isNew,
  readOnly = false,
  dirty,
  busy,
  error,
  keyError,
  badge,
  headerActions,
  description,
  onSubmit,
}: TemplateFormProps) {
  const url = useUrlParams();
  const raw = url.get('channel');
  const channel = TEMPLATE_CHANNELS.find((id) => id === raw) ?? DEFAULT_CHANNEL;
  const setChannel = (next: NotificationChannel) =>
    url.set({ channel: next === DEFAULT_CHANNEL ? null : next });

  const schema = useMemo(() => templateSchema(isNew), [isNew]);
  const check = useFormValidation(schema, draft, (field) => `template-${field.replace('.', '-')}`);

  const values = draft[channel];
  const patchChannel = (changes: Partial<ChannelFormValues>) =>
    patch({ [channel]: { ...values, ...changes } });
  const sampleKey = `${channel}.sampleData`;
  const [pickingDesign, setPickingDesign] = useState(false);

  const submit = () => {
    // Surface an error on a tab other than the open one before the submit reports it.
    const first = Object.keys(fieldErrorsFor(draft, isNew))[0] ?? '';
    const failing = TEMPLATE_CHANNELS.find((id) => first.startsWith(`${id}.`));
    if (failing) setChannel(failing);
    if (check.submit()) onSubmit();
  };

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <form
        id={FORM_ID}
        noValidate
        className="xl:col-span-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (!readOnly) submit();
        }}
      >
        <Card>
          <CardHeader
            title="Message"
            description={description}
            badge={badge}
            actions={headerActions}
          />
          <CardBody className="space-y-6">
            {error && <Alert variant="error" message={error} />}

            <CardSection title="Basics">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <TextField
                  id="template-name"
                  testId="template-name"
                  label="Name"
                  required
                  readOnly={readOnly}
                  value={draft.name}
                  onChange={(name) => patch(isNew ? renameTemplate(draft, name) : { name })}
                  onBlur={() => check.touch('name')}
                  error={check.errors.name}
                  helperText="Shown in lists and breadcrumbs."
                />
                <TextField
                  id="template-templateKey"
                  testId="template-templateKey"
                  label="Template key"
                  required={isNew}
                  code
                  readOnly={!isNew}
                  value={draft.templateKey}
                  onChange={(templateKey) => patch({ templateKey })}
                  transform={(value) => value.toLowerCase().replace(/-/g, '_')}
                  transformHint={isNew ? KEY_DASH_HINT : undefined}
                  onBlur={() => check.touch('templateKey')}
                  error={keyError ?? check.errors.templateKey}
                  helperText={
                    isNew
                      ? 'Filled in from the name. Lowercase letters, numbers, . and _. What a send names to pick this template; it cannot change later.'
                      : 'What a send names to pick this template. It cannot change after creation.'
                  }
                />
                <div className="lg:col-span-2">
                  <Textarea
                    id="template-description"
                    testId="template-description"
                    label="Description"
                    rows={2}
                    readOnly={readOnly}
                    value={draft.description}
                    onChange={(value) => patch({ description: value })}
                    helperText="What the template is for and when it is sent."
                  />
                </div>
                <div className="space-y-1 lg:col-span-2">
                  <TagsEditor
                    label="Customer IDs"
                    noun="customer ID"
                    tags={draft.customerIds}
                    onChange={(customerIds) => patch({ customerIds })}
                    readOnly={readOnly}
                    testId="template-customerIds"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {draft.customerIds.length
                      ? 'For these customers it replaces the global template with the same key.'
                      : 'None: a global template, sent for every customer without their own.'}
                  </p>
                </div>
              </div>
            </CardSection>

            <CardSection
              title="Channels"
              description="Write the message for each channel to send on. A channel with no body is left out."
            >
              <Tabs
                tabs={TABS}
                activeId={channel}
                onChange={(id) => setChannel(id as NotificationChannel)}
                ariaLabel="Channel"
                idPrefix="template-channel"
              />
              {check.errors.channels && (
                <p role="alert" className="text-sm text-error-600 dark:text-error-400">
                  {check.errors.channels}
                </p>
              )}
              <div
                role="tabpanel"
                id={`template-channel-panel-${channel}`}
                aria-labelledby={`template-channel-tab-${channel}`}
                className="space-y-6"
              >
                {channel === 'email' && (
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <TextField
                      id="template-email-subject"
                      testId="template-email-subject"
                      label="Subject"
                      readOnly={readOnly}
                      value={values.subject}
                      onChange={(subject) => patchChannel({ subject })}
                    />
                    <TextField
                      id="template-email-from"
                      testId="template-email-from"
                      label="From"
                      readOnly={readOnly}
                      value={values.from}
                      onChange={(from) => patchChannel({ from })}
                      placeholder="Uses the provider key's default"
                    />
                  </div>
                )}
                {channel === 'email' && values.designTemplateId ? (
                  <LinkedDesign
                    designTemplateId={values.designTemplateId}
                    readOnly={readOnly}
                    onChange={() => setPickingDesign(true)}
                    onRemove={() => patchChannel({ designTemplateId: '' })}
                  />
                ) : channel === 'email' ? (
                  <div className="space-y-3">
                    {!readOnly && (
                      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-gray-300 px-4 py-3 dark:border-gray-700">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Write the body below, or use a design from the design library.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPickingDesign(true)}
                          testId="template-design-choose"
                        >
                          Choose design
                        </Button>
                      </div>
                    )}
                    <RichTextField
                      id="template-email-body"
                      testId="template-email-body"
                      label="Body"
                      disabled={readOnly}
                      value={values.body}
                      onChange={(body) => patchChannel({ body })}
                      helperText="Handlebars: {{name}} fills a value at send time; {{#if}} and {{#each}} work too. HTML the editor cannot hold opens as source."
                    />
                  </div>
                ) : (
                  <Textarea
                    id={`template-${channel}-body`}
                    testId={`template-${channel}-body`}
                    label="Body"
                    code
                    rows={12}
                    readOnly={readOnly}
                    value={values.body}
                    onChange={(body) => patchChannel({ body })}
                    helperText="Plain text. Handlebars: {{name}} fills a value at send time."
                  />
                )}
                <JsonField
                  key={channel}
                  id={`template-${channel}-sampleData`}
                  testId={`template-${channel}-sampleData`}
                  label="Sample data"
                  readOnly={readOnly}
                  value={values.sampleData}
                  onChange={(sampleData) => patchChannel({ sampleData })}
                  onBlur={() => check.touch(sampleKey)}
                  error={check.errors[sampleKey]}
                  helperText="A JSON object the preview fills the placeholders with."
                />
              </div>
            </CardSection>
          </CardBody>
          {!readOnly && (
            <CardFooter>
              <FormActions
                form={FORM_ID}
                discardHref={routes.notificationTemplates}
                {...(isNew
                  ? { saveLabel: 'Create template', busyLabel: 'Creating…' }
                  : {
                      disabled: !dirty,
                      disabledReason: 'No changes to save',
                      saveLabel: 'Save',
                      discardTestId: 'template-discard',
                    })}
                busy={busy}
                saveTestId="template-save"
              />
            </CardFooter>
          )}
        </Card>
      </form>

      <div>
        <TemplateTestPanel
          form={draft}
          channel={channel}
          templateId={templateId}
          dirty={dirty}
          readOnly={readOnly}
        />
      </div>

      <DesignPickerModal
        isOpen={pickingDesign}
        onClose={() => setPickingDesign(false)}
        currentId={draft.email.designTemplateId}
        onAssociate={(design) => patch({ email: { ...draft.email, designTemplateId: design.id } })}
      />
    </div>
  );
}
